/**
 * (C) The phone rings (docs/DESIGN_MEDIA.md, stage 2): a story character calls the player — a job offer, a word at the pickup,
 * a taunt after a raid. The overlay is HTML in the phone's frame (`#call` in index.html): the caller's face (the portrait the
 * owner picked, else a drawn placeholder), the name and role, **Answer** / **Decline**, the phone's chime every two seconds
 * while it rings. Answered: the picked take plays — a talking-head video (stage 3) with its own sound, or the voice line
 * (Chatterbox) under the face — with the line as a subtitle; with nothing rendered, the browser voice reads the line in the
 * character's voice settings. Unanswered for `ring` seconds, or declined, it is a missed call. The server hears the answer
 * (`{t:'call', id, answered}`) and applies the story's consequences; a call that offers a job starts it only when answered.
 */
import type { Registry } from '../content/registry';
import { speech } from '../engine/speech';
import { hashStr } from '../world/rng';
import { lineChoice } from './radio-choice';
import { clearNotice } from './messages';

export interface CallHost {
  reg: Registry;
  /** the rendered take for a media id — an mp4 (a video call) or a wav / mp3 (the voice), or nothing (the placeholder) */
  mediaUrl(id: string): string | undefined;
  /** (owner, Sep 25 — game/radio-choice.ts) whether the server has rendered calls at all: undefined while its media index is on the way
   *  (the call waits a moment), true = a call without its take is the subtitle alone, false / absent = the browser's voice reads it */
  rendered?(): boolean | undefined;
  /** (owner, Sep 25 — Q100) the world's radioFallback setting: a call without its take is read by the browser's voice instead of the subtitle alone */
  fallback?(): boolean;
  /** the character's picked portrait, or nothing (a drawn face) */
  portraitUrl(id: string): string | undefined;
  answer(id: string, answered: boolean): void;
  chime(): void;
}

let host: CallHost | undefined;
let ringing: { id: string; timer: number; until: number } | undefined;
let live: { id: string; el?: HTMLMediaElement; speechEnd?: number } | undefined;
const el = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

export function initCalls(h: CallHost): void {
  host = h;
  el<HTMLButtonElement>('callanswer').addEventListener('pointerdown', (e) => { e.preventDefault(); answer(); });
  el<HTMLButtonElement>('calldecline').addEventListener('pointerdown', (e) => { e.preventDefault(); decline(); });
  // a button keeps the keyboard focus after a tap, and Space would press it again (Space is jump): let go of it
  for (const id of ['callanswer', 'calldecline', 'callhangup']) el<HTMLButtonElement>(id).addEventListener('pointerup', (e) => (e.currentTarget as HTMLElement).blur());
}
export function callOpen(): boolean { return !el<HTMLDivElement>('call').hidden; }
/** a call is up — answered, not merely ringing (owner, Sep 16: the world's sound drops while it lasts; main.ts) */
export function callLive(): boolean { return !!live; }

/** the phone rings: the overlay with the caller, Answer / Decline, the chime until one is pressed or the time is up */
export function ringCall(id: string, ring = 15): void {
  if (!host) return;
  const m = host.reg.get('media', id); if (!m) return;
  const c = m.character ? host.reg.get('character', m.character) : undefined;
  if (ringing || live) hangUp(false); // (one call at a time: a new one takes the phone)
  const box = el<HTMLDivElement>('call');
  el<HTMLDivElement>('callname').textContent = c?.name ?? m.from ?? 'Unknown number';
  el<HTMLDivElement>('callrole').textContent = c?.role ?? '';
  el<HTMLDivElement>('callstatus').textContent = 'incoming call';
  el<HTMLDivElement>('callline').textContent = '';
  const face = el<HTMLImageElement>('callface'), drawn = el<HTMLCanvasElement>('callfacedrawn'), video = el<HTMLVideoElement>('callvideo');
  video.hidden = true; video.removeAttribute('src');
  const url = c ? host.portraitUrl(c.id) : undefined;
  if (url) { face.src = url; face.hidden = false; drawn.hidden = true; }
  else { face.hidden = true; drawn.hidden = false; drawFace(drawn, c?.name ?? m.from ?? '?', c?.id ?? id); }
  el<HTMLDivElement>('callbuttons').hidden = false; el<HTMLButtonElement>('callhangup').hidden = true;
  box.hidden = false;
  const now = performance.now() / 1000;
  host.chime();
  const timer = window.setInterval(() => { if (!ringing) return; if (performance.now() / 1000 > ringing.until) { decline(); return; } host?.chime(); }, 2000);
  ringing = { id, timer, until: now + Math.max(4, ring) };
}

function answer(): void {
  if (!ringing || !host) return;
  const { id } = ringing; window.clearInterval(ringing.timer); ringing = undefined;
  clearNotice(); // (the "is calling" card on the corner phone goes with the ring — owner, Sep 9)
  host.answer(id, true);
  const m = host.reg.get('media', id)!; const c = m.character ? host.reg.get('character', m.character) : undefined;
  el<HTMLDivElement>('callbuttons').hidden = true; el<HTMLButtonElement>('callhangup').hidden = false;
  el<HTMLDivElement>('callstatus').textContent = 'connected';
  el<HTMLDivElement>('callline').textContent = (m.line ?? '').replace(/\[[^\]]*\]/g, '').trim(); // (the subtitle without the [laugh] / [nervous] tags the voice takes)
  live = { id };
  connect(id, c, 0);
}
/**
 * (owner, Sep 25: the browser's voice "should not be playing when there is a rendered one available") the take when there is one; while
 * the media index is on its way, a moment's wait (up to three seconds) rather than the browser's voice; a take that fails to play is the
 * subtitle alone, never the browser's voice in its place; no take on a server that has rendered calls, the subtitle alone too; the
 * browser's voice only where nothing is rendered (a fresh server, single player in dev).
 */
function connect(id: string, c: Parameters<typeof speakLine>[1], tries: number): void {
  if (!host || live?.id !== id) return;
  const line = host.reg.get('media', id)?.line ?? '', url = host.mediaUrl(id);
  let done = false; const quiet = () => { if (done || live?.id !== id) return; done = true; speakLine(line, c, false); };
  if (url && /\.(mp4|webm)$/i.test(url)) {
    // stage 3: the talking head, with its own sound
    const v = el<HTMLVideoElement>('callvideo'); v.src = url; v.hidden = false; el<HTMLImageElement>('callface').hidden = true; el<HTMLCanvasElement>('callfacedrawn').hidden = true;
    v.onended = () => hangUp(true); v.onerror = quiet; void v.play().catch(quiet); live.el = v;
  } else if (url) {
    // stage 2: the voice, under the face
    const a = el<HTMLAudioElement>('callaudio'); a.src = url; a.onended = () => hangUp(true); a.onerror = quiet; void a.play().catch(quiet); live.el = a;
  } else {
    const choice = lineChoice(false, host.rendered ? host.rendered() : false, !!host.fallback?.());
    if (choice === 'wait' && tries < 12) { live.speechEnd = window.setTimeout(() => connect(id, c, tries + 1), 250); return; }
    speakLine(line, c, choice === 'fallback');
  }
}
/** the placeholder: the browser voice reads the line in the character's settings (`voice` false: the subtitle alone); the call ends when it would have */
function speakLine(line: string, c?: { sex: 'f' | 'm'; voice?: { pitch?: number; rate?: number }; id: string }, voice = true): void {
  const words = line.replace(/\[[^\]]*\]/g, '').trim();
  if (words && voice) speech.say(words, { sex: c?.sex ?? 'm', pitch: c?.voice?.pitch ?? 1, rate: c?.voice?.rate ?? 1, seed: hashStr(c?.id ?? 'caller') }, 1, true);
  const secs = Math.max(3, words.length * 0.065 + 1);
  if (live) live.speechEnd = window.setTimeout(() => hangUp(true), secs * 1000);
}
function decline(): void {
  if (!ringing || !host) return;
  const { id } = ringing; window.clearInterval(ringing.timer); ringing = undefined;
  clearNotice();
  el<HTMLDivElement>('call').hidden = true;
  host.answer(id, false);
}
/** the player hangs up, or the line ends */
export function hangUp(ended: boolean): void {
  if (ringing) { window.clearInterval(ringing.timer); const { id } = ringing; ringing = undefined; host?.answer(id, false); }
  if (live) { if (live.el) { live.el.pause(); live.el.removeAttribute('src'); live.el.onended = null; } if (live.speechEnd) window.clearTimeout(live.speechEnd); live = undefined; }
  void ended;
  clearNotice();
  el<HTMLDivElement>('call').hidden = true;
}

/** a drawn face for a character with no picked portrait: a disc in a colour from the id, the initial on it */
function drawFace(cv: HTMLCanvasElement, name: string, id: string): void {
  const W = (cv.width = 160), H = (cv.height = 160), ctx = cv.getContext('2d')!;
  const hue = hashStr(id) % 360;
  ctx.fillStyle = '#151c26'; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = `hsl(${hue} 45% 38%)`; ctx.beginPath(); ctx.arc(W / 2, H / 2, 64, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#e8f0ff'; ctx.font = 'bold 64px system-ui, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(name.trim().charAt(0).toUpperCase() || '?', W / 2, H / 2 + 4);
}

/** The call sits on the corner phone when there is room for it (the owner, Sep 9: "it came in the middle of the screen and
 * blocks you from seeing the game") — the box over the phone's frame, the panel scaled to its width; with no room (a
 * touch phone's small corner, the big phone open) it is the full-screen sheet in the middle. Called every tick with the rect or null. */
let dockKey = '';
export function dockCall(rect: { x: number; y: number; w: number; h: number } | null, opts: { pos?: 'tr' | 'tl' | 'br' | 'bl'; touch?: boolean } = {}): void {
  const pos = opts.pos ?? 'tr', touch = !!opts.touch;
  const key = rect ? `${Math.round(rect.x)},${Math.round(rect.y)},${Math.round(rect.w)},${Math.round(rect.h)},${pos},${touch}` : '';
  if (key === dockKey) return; dockKey = key;
  const box = el<HTMLDivElement>('call'), panel = el<HTMLDivElement>('callpanel');
  if (rect) {
    // wide enough for fingers (the owner, Sep 9: the sheet in the middle "blocks doing anything until you hang up" — you get shot):
    // the box grows from the corner phone toward the middle, the rest of the screen stays the game's
    const w = Math.min(window.innerWidth - 8, Math.max(rect.w, touch ? 240 : 200));
    const fs = Math.max(touch ? 12 : 10, Math.min(15, (w / 340) * 15));
    const left = Math.max(4, pos.endsWith('r') ? rect.x + rect.w - w : rect.x);
    const vert = pos.startsWith('t') ? `top:${Math.round(rect.y)}px` : `bottom:${Math.round(window.innerHeight - rect.y - rect.h)}px`;
    box.style.cssText = `position:fixed;left:${Math.round(left)}px;${vert};width:${Math.round(w)}px;background:transparent;z-index:31;align-items:flex-start;justify-content:center`;
    panel.style.width = '100%'; panel.style.minHeight = ''; panel.style.maxHeight = `calc(100vh - ${Math.round(pos.startsWith('t') ? rect.y : window.innerHeight - rect.y - rect.h) + 8}px)`; panel.style.fontSize = `${fs.toFixed(2)}px`; panel.style.borderRadius = '12px';
  } else {
    box.style.cssText = 'position:fixed;inset:0;align-items:center;justify-content:center;background:rgba(0,0,0,0.6);z-index:31';
    panel.style.width = ''; panel.style.minHeight = ''; panel.style.maxHeight = ''; panel.style.fontSize = ''; panel.style.borderRadius = '';
  }
}
